import React, { useState, useMemo } from 'react';
import AddToCampaignModal from './AddToCampaignModal';

const ExecutiveTable = ({ alumniData = [] }) => {
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  // Filter and transform alumni data to get executives
  const executives = useMemo(() => {
    // Define executive roles
    const executiveRoles = ['CTO', 'CEO', 'CFO', 'CIO', 'COO', 'VP', 'Director', 'President', 'Chief', 'Manager', 'Head', 'Lead'];
    
    // Filter alumni with any LinkedIn data or leadership roles
    return alumniData
      .filter(alumnus => {
        // Include alumni with any LinkedIn data
        if (alumnus.linkedInUrl || alumnus.currentTitle || alumnus.currentCompany) {
          return true;
        }
        
        // Include alumni with leadership roles
        const title = alumnus.currentTitle || '';
        if (executiveRoles.some(role => title.toLowerCase().includes(role.toLowerCase()))) {
          return true;
        }
        
        // Include alumni with leadership scores
        if (alumnus.leadershipScore && alumnus.leadershipScore > 0) {
          return true;
        }
        
        return false;
      })
      .map((alumnus, index) => {
        // Extract role from title
        const title = alumnus.currentTitle || '';
        let role = 'Professional';
        
        // Determine the most senior role if multiple matches
        if (title.includes('CEO') || title.includes('Chief Executive')) {
          role = 'CEO';
        } else if (title.includes('CTO') || title.includes('Chief Technology')) {
          role = 'CTO';
        } else if (title.includes('CFO') || title.includes('Chief Financial')) {
          role = 'CFO';
        } else if (title.includes('CIO') || title.includes('Chief Information')) {
          role = 'CIO';
        } else if (title.includes('COO') || title.includes('Chief Operating')) {
          role = 'COO';
        } else if (title.includes('President')) {
          role = 'President';
        } else if (title.includes('VP') || title.includes('Vice President')) {
          role = 'VP';
        } else if (title.includes('Director')) {
          role = 'Director';
        } else if (title.includes('Chief')) {
          role = 'Chief';
        } else if (title.includes('Head of')) {
          role = 'Head';
        } else if (title.includes('Manager')) {
          role = 'Manager';
        } else if (title.includes('Lead')) {
          role = 'Lead';
        } else if (title.includes('Senior') || title.includes('Sr.')) {
          role = 'Senior';
        }
        
        // Calculate lead score based on role seniority
        let leadScore;
        
        // Set base score by role (much more differentiated)
        if (role === 'CEO') {
          leadScore = 95; // CEO starts with very high score
        } else if (role === 'CTO' || role === 'CFO' || role === 'CIO' || role === 'COO') {
          leadScore = 90; // C-suite executives
        } else if (role === 'President') {
          leadScore = 90; // President level
        } else if (role === 'VP') {
          leadScore = 85; // Vice Presidents
        } else if (role === 'Director') {
          leadScore = 80; // Directors
        } else if (role === 'Chief') {
          leadScore = 85; // Other chief positions
        } else if (role === 'Head') {
          leadScore = 75; // Department heads
        } else if (role === 'Manager') {
          leadScore = 65; // Managers
        } else if (role === 'Lead') {
          leadScore = 60; // Team leads
        } else if (role === 'Senior') {
          leadScore = 55; // Senior individual contributors
        } else {
          leadScore = 50; // Default for professionals
        }
        
        // Add small random variation to prevent identical scores
        leadScore += Math.floor(Math.random() * 5);
        
        // Add points for leadership score if available (minor adjustment)
        if (alumnus.leadershipScore) {
          // Add at most 5 points based on leadership score
          leadScore += Math.min(alumnus.leadershipScore, 5);
        }
        
        // Cap at 100
        leadScore = Math.min(leadScore, 100);
        
        // Calculate project potential based on role
        let potentialMultiplier = 1.0;
        if (title.includes('CTO') || title.includes('CEO')) potentialMultiplier = 3.0;
        else if (title.includes('VP')) potentialMultiplier = 2.0;
        else if (title.includes('Director')) potentialMultiplier = 1.5;
        
        const projectPotential = `$${(potentialMultiplier * 1.2).toFixed(1)}M`;
        
        // Determine tech alignment based on industry or skills
        let techAlignment = 'Medium';
        if (alumnus.industry === 'Technology' || (alumnus.skills && alumnus.skills.some(skill => 
          ['Software', 'IT', 'Technology', 'Programming', 'Development'].includes(skill)))) {
          techAlignment = 'High';
        }
        
        // Calculate last contact date
        // Default to 'Never' unless lastContactDate is present
        let lastContact = 'Never';
        if (alumnus.lastContactDate) {
          const lastContactDate = new Date(alumnus.lastContactDate);
          const now = new Date();
          const diffDays = Math.floor((now - lastContactDate) / (1000 * 60 * 60 * 24));
          
          if (diffDays === 0) lastContact = 'Today';
          else if (diffDays === 1) lastContact = 'Yesterday';
          else if (diffDays < 7) lastContact = `${diffDays} days ago`;
          else if (diffDays < 30) lastContact = `${Math.floor(diffDays / 7)} weeks ago`;
          else lastContact = `${Math.floor(diffDays / 30)} months ago`;
        }
        
        // Determine lead status
        const status = leadScore >= 85 ? 'qualified' : 'potential';
        
        return {
          id: index + 1,
          name: alumnus.emp_empname,
          role,
          company: alumnus.currentCompany || 'Unknown',
          projectPotential,
          leadScore,
          techAlignment,
          lastContact,
          status,
          linkedInUrl: alumnus.linkedInUrl
        };
      })
      .sort((a, b) => b.leadScore - a.leadScore) // Sort by lead score (highest first)
      .slice(0, 20); // Show up to 20 executives
  }, [alumniData]);

  // Function to render status badge
  const renderStatusBadge = (status) => {
    if (status === 'qualified') {
      return <span className="badge badge-success">Qualified</span>;
    } else if (status === 'potential') {
      return <span className="badge badge-warning">Potential</span>;
    }
    return null;
  };

  // Function to render tech alignment badge
  const renderTechBadge = (alignment) => {
    if (alignment === 'High') {
      return <span className="badge badge-success">High</span>;
    } else if (alignment === 'Medium') {
      return <span className="badge badge-info">Medium</span>;
    } else {
      return <span className="badge badge-warning">Low</span>;
    }
  };

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>Name / Role</th>
            <th>Current Company</th>
            <th>Project Potential</th>
            <th>Lead Score</th>
            <th>Tech Alignment</th>
            <th>Last Contact</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {executives.map((executive) => (
            <tr key={executive.id}>
              <td>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{executive.name}</span>
                  <span className="text-xs">{executive.role}</span>
                  {renderStatusBadge(executive.status)}
                </div>
              </td>
              <td>{executive.company}</td>
              <td>
                <span className="font-medium">{executive.projectPotential}</span>
                <div className="flex mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg 
                      key={star} 
                      className={`w-4 h-4 ${star <= Math.floor(executive.leadScore / 20) ? 'text-yellow-400' : 'text-gray-300'}`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </td>
              <td>{executive.leadScore}</td>
              <td>{renderTechBadge(executive.techAlignment)}</td>
              <td>{executive.lastContact}</td>
              <td>
                <div className="flex space-x-2">
                  {executive.linkedInUrl ? (
                    <a 
                      href={executive.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 rounded-full bg-blue-100 hover:bg-blue-200"
                      title="View LinkedIn Profile"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                    </a>
                  ) : (
                    <button 
                      className="p-1 rounded-full bg-gray-200 hover:bg-gray-300"
                      title="View Details"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                    </button>
                  )}
                  <button 
                    className="p-1 rounded-full bg-green-100 hover:bg-green-200"
                    title="Mark as Qualified"
                  >
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </button>
                  <button 
                    className="p-1 rounded-full bg-blue-100 hover:bg-blue-200"
                    title="Contact"
                    onClick={() => {
                      // Create a new array with the updated alumnus
                      const updatedAlumni = alumniData.map(a => {
                        if (a.emp_empname === executive.name) {
                          // Create a new object with the lastContactDate set to now
                          return {
                            ...a,
                            lastContactDate: new Date().toISOString()
                          };
                        }
                        return a;
                      });
                      
                      // Save to localStorage
                      localStorage.setItem('alumni_data', JSON.stringify(updatedAlumni));
                      
                      // Alert the user
                      alert(`Contact initiated with ${executive.name}`);
                    }}
                  >
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                  <button 
                    className="p-1 rounded-full bg-purple-100 hover:bg-purple-200"
                    title="Add to Campaign"
                    onClick={() => {
                      setSelectedExecutive(executive);
                      setShowCampaignModal(true);
                    }}
                  >
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    
      {/* Add to Campaign Modal */}
      {showCampaignModal && selectedExecutive && (
        <AddToCampaignModal
          onClose={() => setShowCampaignModal(false)}
          onSave={(campaign, executive) => {
            // Find the alumnus in the alumni data
            const alumnus = alumniData.find(a => a.emp_empname === executive.name);
            
            if (alumnus) {
              // Get existing campaign contacts
              const campaignContactsKey = `campaign_contacts_${campaign.id}`;
              const existingContacts = localStorage.getItem(campaignContactsKey);
              const contacts = existingContacts ? JSON.parse(existingContacts) : [];
              
              // Check if the contact already exists in the campaign
              const contactExists = contacts.some(c => c.id === alumnus._id);
              
              if (!contactExists) {
                // Create a new contact object
                const newContact = {
                  id: alumnus._id,
                  name: alumnus.emp_empname,
                  email: alumnus.emp_email,
                  title: alumnus.currentTitle || executive.role,
                  company: alumnus.currentCompany || executive.company,
                  status: 'pending',
                  lastContactDate: null,
                  notes: '',
                  linkedInUrl: alumnus.linkedInUrl || '',
                  addedAt: new Date().toISOString()
                };
                
                // Add to campaign contacts
                const updatedContacts = [...contacts, newContact];
                localStorage.setItem(campaignContactsKey, JSON.stringify(updatedContacts));
                
                // Update campaign stats
                const savedCampaigns = localStorage.getItem('campaigns');
                if (savedCampaigns) {
                  const campaignsList = JSON.parse(savedCampaigns);
                  const updatedCampaigns = campaignsList.map(c => {
                    if (c.id === campaign.id) {
                      return {
                        ...c,
                        // Don't increment completedCount since status is 'pending'
                      };
                    }
                    return c;
                  });
                  localStorage.setItem('campaigns', JSON.stringify(updatedCampaigns));
                }
                
                alert(`${executive.name} has been added to the "${campaign.name}" campaign.`);
              } else {
                alert(`${executive.name} is already in the "${campaign.name}" campaign.`);
              }
            }
            
            setShowCampaignModal(false);
          }}
          executive={selectedExecutive}
        />
      )}
    </div>
  );
};

export default ExecutiveTable;
